import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useCryptoStore, isStaleKeyPair } from '../../stores/cryptoStore';
import { useKeyInit } from '../../hooks/useKeyInit';
import { PasswordInput } from '../ui/PasswordInput';
import api from '../../lib/api';

/**
 * Blocking prompt shown when the user has a session but no private key.
 *
 * authStore is persisted to localStorage; cryptoStore is deliberately
 * in-memory only, so the private key is destroyed on every page refresh,
 * tab close, and PWA restart. Before this existed, initKeys() was reachable
 * from LoginPage alone, which meant:
 *
 *   - after any refresh the user looked signed in but could not encrypt or
 *     decrypt anything, and
 *   - a user who arrived via RegisterPage or JoinPage never had a key bundle
 *     created at all, so they had no published public key and admins could
 *     not seal a channel key to them.
 *
 * The only way out was to sign out and back in, which is what the old
 * "Your device's encryption key isn't loaded" toast told people to do.
 *
 * initKeys() covers both cases: it unwraps the existing bundle with a
 * PBKDF2-derived KEK, or generates and uploads a new keypair when the server
 * has no bundle yet. The password lives in component state only — it is never
 * persisted, and neither is the derived key.
 */
export default function UnlockEncryption() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const logout = useAuthStore((s) => s.logout);
  const secretKey = useCryptoStore((s) => s.secretKey);
  const publicKey = useCryptoStore((s) => s.publicKey);
  const clearKeys = useCryptoStore((s) => s.clearKeys);
  const { initKeys } = useKeyInit();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [staleKeyDetected, setStaleKeyDetected] = useState(false);
  const staleCheckRan = useRef(false);

  // One-shot freshness check: only when a key is already cached (the branch
  // that would otherwise return null below) does it make sense to ask
  // whether that cached key predates a password reset. Guarded by a ref so
  // it never re-fires on re-renders, and a cancelled flag so a response that
  // arrives after unmount is a no-op.
  useEffect(() => {
    if (!accessToken || !secretKey || staleCheckRan.current) return;
    staleCheckRan.current = true;
    let cancelled = false;

    (async () => {
      try {
        const { data } = await api.get<{ success: true; data: { publicKey: string } | null }>(
          '/api/v1/auth/keys',
        );
        if (cancelled) return;
        if (isStaleKeyPair(publicKey, data.data?.publicKey ?? null)) {
          clearKeys();
          setStaleKeyDetected(true);
        }
      } catch {
        // Fail open: a network hiccup must never force an unnecessary
        // re-unlock.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessToken, secretKey, publicKey, clearKeys]);

  // Signed out, or keys already loaded — nothing to do.
  if (!accessToken || secretKey) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!password || loading) return;
    setError('');
    setLoading(true);
    try {
      await initKeys(password);
      setPassword('');
    } catch {
      // initKeys throws when the KEK cannot decrypt the stored private key,
      // which in practice means the password was wrong.
      setError('That password did not unlock your encryption key. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="unlock-encryption-title"
    >
      <div className="w-full max-w-sm rounded-2xl bg-warm-white p-6 shadow-warm-lg dark:bg-warm-dark-surface">
        <h2
          id="unlock-encryption-title"
          className="text-lg font-bold text-warm-gray-900 dark:text-warm-white"
        >
          Unlock encryption
        </h2>
        <p className="mt-2 text-sm text-warm-gray-600 dark:text-warm-gray-300">
          Your messages are end-to-end encrypted, so your key is never stored on this device. Enter
          your password to unlock it for this session.
        </p>

        {staleKeyDetected && (
          <p role="alert" className="mt-2 text-sm text-amber-600 dark:text-amber-400">
            Your saved encryption key doesn't match your account anymore, which usually happens
            after a password reset on another device. Enter your password to unlock the current key.
          </p>
        )}

        <form onSubmit={handleSubmit} className="mt-4">
          <label htmlFor="unlock-password" className="sr-only">
            Password
          </label>
          <PasswordInput
            id="unlock-password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-warm-gray-300 px-3 py-2 text-warm-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-warm-gray-600 dark:bg-warm-dark-elevated dark:text-warm-white"
            placeholder="Your password"
          />

          {error && (
            <p role="alert" className="mt-2 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={!password || loading}
            className="mt-4 w-full rounded-lg bg-primary px-4 py-2 font-medium text-white transition hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Unlocking…' : 'Unlock'}
          </button>
        </form>

        {/* Escape hatches — this dialog was previously a dead end: a user
            whose password no longer unlocks the key (typo'd, or reset while
            they were away) had no way to reset or even sign out. */}
        <div className="mt-4 flex items-center justify-between border-t border-warm-gray-200 pt-4 dark:border-warm-gray-700">
          <Link to="/forgot-password" className="text-sm font-medium text-primary hover:underline">
            Forgot password?
          </Link>
          <button
            type="button"
            onClick={logout}
            className="text-sm font-medium text-warm-gray-500 hover:text-warm-gray-700 hover:underline dark:text-warm-gray-400 dark:hover:text-warm-gray-200"
          >
            Sign out
          </button>
        </div>
        <p className="mt-3 text-xs text-warm-gray-500 dark:text-warm-gray-400">
          Resetting your password creates a new encryption key — messages sent before the reset
          can't be read with the new key.
        </p>
      </div>
    </div>
  );
}
